import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkAuthWithBackend } from "../../../services/authApiService";
import { getActiveGiftTableByUserID } from "../../../services/giftTableApiService";
import { login } from "../../../redux/slices/authSlice";
import { fetchAndLoadGiftTable } from "../../../redux/slices/giftTableSlice";
import { deleteCookie, getCookie, setCookie } from "../../../utils/cookieUtils"; // ← added setCookie
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { resetClientStorage } from "../../../utils/resetClientStorage";
import { resetApp } from "../../../redux/store";
import i18n, { i18nInstance } from "../../../i18n";

const normalizeLanguage = (language?: string | null) =>
  language?.startsWith("en") ? "en" : "es";

const InitPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      // 1️⃣ try to grab token from URL

      const params = new URLSearchParams(window.location.search);
      let shouldCleanUrl = false;
      const redirectTo = params.get("redirect");

      const urlLang = params.get("lang");
      if (urlLang) {
        const normalizedLang = normalizeLanguage(urlLang);
        localStorage.setItem("language", normalizedLang);
        localStorage.setItem("languageConfirmed", "true");
        await i18nInstance;
        await i18n.changeLanguage(normalizedLang);
        params.delete("lang");
        shouldCleanUrl = true;
      }

      const urlToken = params.get("authToken");
      const jwtToken = urlToken ?? getCookie("authToken");

      // if we got it from URL, persist to cookie and clean up URL
      if (urlToken) {
        setCookie("authToken", urlToken, {
          path: "/",
          secure: true,
          sameSite: "strict",
          maxAge: 60 * 60 * 24 * 7,
        }); // keep for 7 days
        params.delete("authToken");
        shouldCleanUrl = true;
      }

      if (shouldCleanUrl) {
        const newSearch = params.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`
        );
      }

      if (!jwtToken) {
        await resetClientStorage({ preserveLocalStorageKeys: ["language"] });
        dispatch(resetApp());
        window.location.href = import.meta.env.VITE_LANDING_APP_URL;
        return;
      }

      try {
        // Validate the user's authentication
        const authResponse = await checkAuthWithBackend(jwtToken);

        await resetClientStorage({ preserveLocalStorageKeys: ["language"] });
        dispatch(resetApp());

        dispatch(
          login({
            id: authResponse.user.id,
            email: authResponse.user.email,
            name: authResponse.user.name as string,
            photoUrl: authResponse.user.avatar,
            acceptsTerms: authResponse.user.accepts_terms,
            acceptsPrivacy: authResponse.user.accepts_privacy,
          })
        );

        // Check for an active gift table
        const activeTableID = await getActiveGiftTableByUserID(
          authResponse.user.id
        );

        if (redirectTo) {
          // Redirect to external/internal URL provided
          window.location.href = redirectTo;
          return;
        }

        if (activeTableID) {
          dispatch(fetchAndLoadGiftTable(activeTableID));
          navigate("/my-table");
        } else if (
          !authResponse.user.accepts_terms ||
          !authResponse.user.accepts_privacy
        ) {
          navigate("/registration/accept-terms");
        } else {
          navigate("/registration/create-gift-table/step1");
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error(
          "Error during initialization",
          (error as AxiosError).response?.status
        );
        if ((error as AxiosError).response?.status === 401) {
          deleteCookie("authToken");
        }
        window.location.href = import.meta.env.VITE_LANDING_APP_URL;
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [dispatch, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return null;
};

export default InitPage;
